import {
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  onMount,
  Show,
  untrack,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { SectorSelector } from "@/domains/freelance/taxonomy/components/sector-selector";
import { createSectorDraft } from "@/domains/freelance/taxonomy/selector/sector-draft";
import {
  categoryOptions,
  parentOf,
  subcategoriesOf,
  taxonomyStatus,
} from "@/domains/freelance/taxonomy/store/taxonomy-store";
import { useOnboardingStore } from "../../context/OnboardingContext";
import {
  nextSectorError,
  sectorDraftInputFromStore,
  sectorValueFromSlots,
} from "../../utils/sector-step";
import { OnboardingDescription, OnboardingTitle } from "../shared/text";

const TAXONOMY = { subcategoriesOf, parentOf };

/** The tree is in: the status says so, or one was stored without a load. */
const treeReady = () =>
  taxonomyStatus() === "ready" ||
  categoryOptions().some((option) => subcategoriesOf(option.value).length > 0);

type SectorStepProps = {
  /**
   * How far the fixed bottom bar covers the scroll area, so the selector's
   * scroll to the secondary leaves it above the bar (AC6). Measured by the
   * route.
   */
  bottomInset?: number;
};

/**
 * Step 2/12 «Settore» (AC16), also what `?edit=sector` shows from the review.
 * The sector selector in its onboarding container, over `store.sector`:
 * every change writes the value (with origins) and the error code back, and
 * the route's bar reads both. No Jev proposal here: during onboarding `/me`
 * has no `suggested_job_category` (PLAN §4.7), so the CV / LinkedIn
 * suggestions are the store's `suggested` subcategories and AC35 never fires.
 *
 * The error codes live in the store (`required`, `sector_reset`) and the text
 * here: `sector_reset` (AC20) shows from the start; `required` only after the
 * freelancer has emptied the primary, since the untouched step already says
 * what to do and «Avanti» is off.
 */
export const Sector = (props: SectorStepProps) => {
  const [store, setStore] = useOnboardingStore();
  const { t } = useI18n();
  const [message, setMessage] = createSignal("");
  const [touched, setTouched] = createSignal(false);

  // Without a primary «Avanti» is off (the store carries the code); a reset
  // code from the sanitizer is kept (AC20).
  onMount(() => {
    setStore(
      "sector",
      "error",
      nextSectorError(store.sector.value, store.sector.error),
    );
  });

  // The layout's fixed header covers the top of the route's scroll area: the
  // scroll to the secondary must leave its title below it (AC6, AC26). It
  // grows with the font size, so it is measured, not assumed.
  const [headerInset, setHeaderInset] = createSignal(0);
  onMount(() => {
    const header = document.querySelector<HTMLElement>(
      "[data-onboarding-header]",
    );
    if (!header) return;
    const measure = () =>
      setHeaderInset(
        Math.max(0, Math.round(header.getBoundingClientRect().bottom)),
      );
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    onCleanup(() => observer.disconnect());
  });

  // Latched: once the tree is there the selector stays mounted, so a later
  // reload of the taxonomy does not rebuild the draft under the freelancer.
  const ready = createMemo<boolean>((was) => was || treeReady(), false);

  const errorText = () => {
    const code = store.sector.error;
    if (code === "sector_reset") return t("onboarding.steps.sector.reset");
    if (code === "required" && touched()) {
      return t("onboarding.steps.sector.required");
    }
    return "";
  };

  return (
    <div class="flex w-full flex-col gap-8">
      <div class="flex flex-col gap-4">
        <OnboardingTitle>{t("onboarding.steps.sector.title")}</OnboardingTitle>
        <OnboardingDescription>
          {t("onboarding.steps.sector.description")}
        </OnboardingDescription>
        <Show when={errorText()}>
          <p role="alert" class="text-sm text-red-600">
            {errorText()}
          </p>
        </Show>
      </div>
      <div aria-live="polite" class="sr-only">
        {message()}
      </div>
      <Show
        when={ready()}
        fallback={
          // Loading / failed: the selector draws its own skeleton, error and
          // «Riprova». The draft is empty, nothing is written from here.
          <SectorSelector
            class="max-w-3xl"
            draft={createSectorDraft(
              sectorDraftInputFromStore({
                value: untrack(() => store.sector.value),
                source: untrack(() => store.sector.source),
              }),
              TAXONOMY,
            )}
            announce={setMessage}
          />
        }
      >
        <SectorPicker
          bottomInset={props.bottomInset}
          topInset={headerInset()}
          announce={setMessage}
          onChange={() => setTouched(true)}
        />
      </Show>
    </div>
  );
};

/**
 * The draft is built once from the store, when the tree is in (it routes each
 * subcategory to its sector), and written back on every change after that.
 */
const SectorPicker = (props: {
  bottomInset?: number;
  topInset?: number;
  announce: (text: string) => void;
  onChange: () => void;
}) => {
  const [store, setStore] = useOnboardingStore();

  const draft = createSectorDraft(
    sectorDraftInputFromStore({
      value: untrack(() => store.sector.value),
      source: untrack(() => store.sector.source),
    }),
    TAXONOMY,
  );

  const value = createMemo(() => sectorValueFromSlots(draft.slots));
  createEffect(
    on(
      value,
      (next) => {
        setStore("sector", {
          value: next,
          error: nextSectorError(
            next,
            untrack(() => store.sector.error),
          ),
        });
        props.onChange();
      },
      { defer: true },
    ),
  );

  return (
    <SectorSelector
      class="max-w-3xl"
      draft={draft}
      announce={props.announce}
      bottomInset={props.bottomInset}
      topInset={props.topInset}
    />
  );
};
